import {
  ArrowLeft,
  ArrowRight,
  Clock3,
  ExternalLink,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import {
  archiveBusinessCenterProject,
  createBusinessCenterProject,
  getAdminClients,
  getBusinessCenterProject,
  getBusinessCenterProjects,
  updateBusinessCenterProject,
} from "../../lib/api";
import { formatDate } from "../../lib/format";
import type {
  BusinessCenterProjectDetail,
  BusinessCenterProjectRecord,
  ClientRecord,
} from "../../lib/types";

type BrandValue = "kreatethedate" | "truevance_connect";

const brandOptions: Array<{ value: BrandValue; label: string; accent: string }> = [
  { value: "truevance_connect", label: "Truevance Connect", accent: "#06B6D4" },
  { value: "kreatethedate", label: "Kreatethedate", accent: "#FF6B35" },
];

const projectStatusOptions = ["Planning", "Active", "Review", "Complete", "Archived"];
const projectPhaseOptions = ["Planning", "Discovery", "Assessment", "Recommendations", "Proposal", "Implementation", "Optimization", "Completed"];
const paymentStatusOptions = ["Pending", "Deposit Due", "Partial Payment Received", "Paid In Full", "Overdue", "Refunded", "Cancelled"];
const portalAccessOptions = ["preparation", "ready", "invite_sent", "active", "paused", "revoked"];

interface ProjectFormState {
  brand: BrandValue;
  clientId: string;
  clientName: string;
  projectName: string;
  projectType: string;
  projectStatus: string;
  projectPhase: string;
  paymentStatus: string;
  assignedTeam: string;
  assignedServicesText: string;
  milestonesText: string;
  deliverablesText: string;
  completionPercent: string;
  revenueCents: string;
  startDate: string;
  dueDate: string;
  portalAccessStatus: string;
  portalAccessNotes: string;
  notes: string;
  activityNote: string;
}

function blankCreateForm(brand: BrandValue): ProjectFormState {
  return {
    brand,
    clientId: "",
    clientName: "",
    projectName: "",
    projectType: "Business Project",
    projectStatus: "Planning",
    projectPhase: "Planning",
    paymentStatus: "Pending",
    assignedTeam: "",
    assignedServicesText: "",
    milestonesText: "",
    deliverablesText: "",
    completionPercent: "0",
    revenueCents: "0",
    startDate: "",
    dueDate: "",
    portalAccessStatus: "preparation",
    portalAccessNotes: "",
    notes: "",
    activityNote: "",
  };
}

function formFromProject(project: BusinessCenterProjectRecord, brand: BrandValue): ProjectFormState {
  return {
    brand,
    clientId: project.clientId || "",
    clientName: project.clientName || "",
    projectName: project.projectName || "",
    projectType: project.projectType || "",
    projectStatus: project.projectStatus || "Planning",
    projectPhase: project.projectPhase || "Planning",
    paymentStatus: project.paymentStatus || "Pending",
    assignedTeam: project.assignedTeam || "",
    assignedServicesText: project.assignedServices.join("\n"),
    milestonesText: project.milestones.join("\n"),
    deliverablesText: project.deliverables.join("\n"),
    completionPercent: String(project.completionPercent || 0),
    revenueCents: String(project.revenueCents || 0),
    startDate: project.startDate || "",
    dueDate: project.dueDate || "",
    portalAccessStatus: project.portalAccessStatus || "preparation",
    portalAccessNotes: project.portalAccessNotes || "",
    notes: project.notes || "",
    activityNote: "",
  };
}

export function BusinessCenterOSProjectCenterPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [selectedBrand, setSelectedBrand] = useState<BrandValue>("truevance_connect");
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [projects, setProjects] = useState<BusinessCenterProjectRecord[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [detail, setDetail] = useState<BusinessCenterProjectDetail | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<ProjectFormState>(blankCreateForm("truevance_connect"));
  const [editForm, setEditForm] = useState<ProjectFormState | null>(null);

  useBusinessCenterOSBranding("Project Center", "Brand-scoped project records, milestones, deliverables, tasks, and progress tracking for Business Center OS.");

  useEffect(() => {
    let cancelled = false;
    setLoadingProjects(true);
    setMessage("");
    Promise.all([
      getBusinessCenterProjects({ brand: selectedBrand, query }),
      getAdminClients({ brand: selectedBrand }),
    ])
      .then(([workspace, brandClients]) => {
        if (cancelled) return;
        setProjects(workspace.projects);
        setClients(brandClients);
        const nextId = projectId || workspace.projects[0]?.id || "";
        setSelectedProjectId((current) => (workspace.projects.some((project) => project.id === current) ? current : nextId));
      })
      .catch((error) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "Unable to load project records.");
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, query, selectedBrand]);

  useEffect(() => {
    if (!selectedProjectId) {
      setDetail(null);
      setEditForm(null);
      return;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setMessage("");
    getBusinessCenterProject(selectedProjectId)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        setEditForm(formFromProject(result.project, (result.project.brand === "truevance_connect" ? "truevance_connect" : "kreatethedate") as BrandValue));
      })
      .catch((error) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "Unable to load project detail.");
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  const summary = useMemo(() => {
    const metrics = projects.reduce(
      (acc, project) => {
        acc.total += 1;
        acc.revenue += Number(project.revenueCents || 0);
        if (project.projectStatus === "Planning") acc.planning += 1;
        if (project.projectStatus === "Active") acc.active += 1;
        if (project.projectStatus === "Review") acc.review += 1;
        if (project.projectStatus === "Complete") acc.complete += 1;
        if (project.projectStatus === "Archived" || project.archivedAt) acc.archived += 1;
        return acc;
      },
      { total: 0, planning: 0, active: 0, review: 0, complete: 0, archived: 0, revenue: 0 },
    );
    return [
      { label: "Projects", value: metrics.total, note: "Brand-scoped project records in the current workspace." },
      { label: "Planning", value: metrics.planning, note: "Projects preparing for delivery." },
      { label: "Active", value: metrics.active, note: "Projects in active execution." },
      { label: "Revenue", value: formatCurrency(metrics.revenue), note: "Tracked revenue on project records." },
    ];
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return projects;
    return projects.filter((project) =>
      [
        project.projectNumber,
        project.clientName,
        project.projectName,
        project.projectType,
        project.projectStatus,
        project.projectPhase,
        project.paymentStatus,
        project.assignedTeam,
        project.notes,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [projects, query]);

  const selectedProject = projects.find((project) => project.id === selectedProjectId) || detail?.project || null;

  function updateCreateForm<K extends keyof ProjectFormState>(key: K, value: ProjectFormState[K]) {
    setCreateForm((current) => ({ ...current, [key]: value }));
  }

  function updateEditForm<K extends keyof ProjectFormState>(key: K, value: ProjectFormState[K]) {
    setEditForm((current) => (current ? { ...current, [key]: value } : current));
  }

  async function handleCreateProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreating(true);
    setMessage("");
    try {
      const created = await createBusinessCenterProject({
        brand: createForm.brand,
        clientId: createForm.clientId || null,
        clientBrand: createForm.brand,
        clientName:
          createForm.clientName ||
          clients.find((client) => client.id === createForm.clientId)?.clientName ||
          "Unnamed Client",
        projectName: createForm.projectName || "Untitled Project",
        projectType: createForm.projectType || "Business Project",
        projectStatus: createForm.projectStatus || "Planning",
        projectPhase: createForm.projectPhase || "Planning",
        paymentStatus: createForm.paymentStatus || "Pending",
        assignedTeam: createForm.assignedTeam || "",
        assignedServices: splitList(createForm.assignedServicesText),
        milestones: splitList(createForm.milestonesText),
        deliverables: splitList(createForm.deliverablesText),
        completionPercent: Number(createForm.completionPercent || 0),
        revenueCents: Number(createForm.revenueCents || 0),
        startDate: createForm.startDate || null,
        dueDate: createForm.dueDate || null,
        portalAccessStatus: createForm.portalAccessStatus || "preparation",
        portalAccessNotes: createForm.portalAccessNotes || "",
        notes: createForm.notes || "",
      });
      const project = created.project.project;
      setMessage(`Project ${project.projectNumber} was created inside the ${brandLabel(createForm.brand)} workspace.`);
      setShowCreateForm(false);
      setCreateForm(blankCreateForm(createForm.brand));
      const [workspace, brandClients, freshDetail] = await Promise.all([
        getBusinessCenterProjects({ brand: createForm.brand, query }),
        getAdminClients({ brand: createForm.brand }),
        getBusinessCenterProject(project.id),
      ]);
      setProjects(workspace.projects);
      setClients(brandClients);
      setDetail(freshDetail);
      setEditForm(freshDetail ? formFromProject(freshDetail.project, createForm.brand) : null);
      setSelectedProjectId(project.id);
      navigate(`/admin/truevance-project-center/${project.id}`, { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to create project.");
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedProject || !editForm) return;
    setSaving(true);
    setMessage("");
    try {
      const updated = await updateBusinessCenterProject(selectedProject.id, {
        clientId: editForm.clientId || null,
        clientBrand: editForm.brand,
        clientName:
          editForm.clientName ||
          clients.find((client) => client.id === editForm.clientId)?.clientName ||
          selectedProject.clientName,
        projectName: editForm.projectName || selectedProject.projectName,
        projectType: editForm.projectType || selectedProject.projectType,
        projectStatus: editForm.projectStatus,
        projectPhase: editForm.projectPhase,
        paymentStatus: editForm.paymentStatus,
        assignedTeam: editForm.assignedTeam,
        assignedServices: splitList(editForm.assignedServicesText),
        milestones: splitList(editForm.milestonesText),
        deliverables: splitList(editForm.deliverablesText),
        completionPercent: Number(editForm.completionPercent || 0),
        revenueCents: Number(editForm.revenueCents || 0),
        startDate: editForm.startDate || null,
        dueDate: editForm.dueDate || null,
        portalAccessStatus: editForm.portalAccessStatus,
        portalAccessNotes: editForm.portalAccessNotes,
        notes: editForm.notes,
        activityNote: editForm.activityNote,
      });
      const project = updated.project;
      const [workspace, freshDetail] = await Promise.all([
        getBusinessCenterProjects({ brand: selectedBrand, query }),
        getBusinessCenterProject(project.id),
      ]);
      setProjects(workspace.projects);
      setDetail(freshDetail);
      setEditForm(formFromProject(project, (project.brand === "truevance_connect" ? "truevance_connect" : "kreatethedate") as BrandValue));
      setSelectedProjectId(project.id);
      setMessage(`${project.projectNumber} was updated and the activity timeline was refreshed.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update project.");
    } finally {
      setSaving(false);
    }
  }

  async function handleArchiveProject() {
    if (!selectedProject) return;
    setArchiving(true);
    setMessage("");
    try {
      await archiveBusinessCenterProject(selectedProject.id);
      const workspace = await getBusinessCenterProjects({ brand: selectedBrand, query });
      setProjects(workspace.projects);
      setSelectedProjectId(workspace.projects[0]?.id || "");
      setDetail(workspace.projects[0] ? await getBusinessCenterProject(workspace.projects[0].id) : null);
      setEditForm(workspace.projects[0] ? formFromProject(workspace.projects[0], selectedBrand) : null);
      setMessage(`${selectedProject.projectNumber} was archived.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to archive project.");
    } finally {
      setArchiving(false);
    }
  }

  async function handleRefreshActivity() {
    if (!selectedProjectId) return;
    setLoadingDetail(true);
    try {
      const freshDetail = await getBusinessCenterProject(selectedProjectId);
      setDetail(freshDetail);
      if (freshDetail) {
        setEditForm(formFromProject(freshDetail.project, (freshDetail.project.brand === "truevance_connect" ? "truevance_connect" : "kreatethedate") as BrandValue));
      }
    } finally {
      setLoadingDetail(false);
    }
  }

  return (
    <BusinessCenterOSAdminShell
      title="Project Center"
      description="Track brand-scoped projects from accepted proposals through delivery, milestones, deliverables, payment progress, and follow-up preparation in one operational workspace."
      statusItems={summary.map(({ label, value }) => ({ label, value }))}
    >
      <section className="py-2">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[.9fr_1.1fr]">
          <aside className="grid gap-6">
            <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 border-b border-[rgba(6,182,212,.16)] pb-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Workspace brand</p>
                  <h2 className="mt-1 font-display text-3xl font-bold">Brand scope</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreateForm((current) => !current)}
                  className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
                >
                  <Plus className="size-4" aria-hidden="true" />
                  {showCreateForm ? "Hide create form" : "New project"}
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {brandOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSelectedBrand(option.value);
                      setCreateForm(blankCreateForm(option.value));
                    }}
                    className={`business-center-os-pill focus-ring inline-flex min-h-10 items-center rounded-full px-4 text-sm font-bold transition ${
                      selectedBrand === option.value ? "business-center-os-pill--active" : ""
                    }`}
                    style={{ borderColor: option.accent, boxShadow: selectedBrand === option.value ? `0 0 0 1px ${option.accent} inset` : undefined }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#22E0D0]" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search projects, clients, phases, or notes"
                    className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 w-full rounded-2xl pl-11 pr-4 text-sm font-semibold"
                  />
                </div>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {summary.slice(0, 2).map((item) => (
                  <div key={item.label} className="rounded-2xl border border-[rgba(6,182,212,.12)] bg-[rgba(255,255,255,.05)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#06B6D4]">{item.label}</p>
                    <p className="mt-2 font-display text-3xl font-bold text-[#F8FAFC]">{item.value}</p>
                  </div>
                ))}
              </div>
            </section>

            {showCreateForm ? (
              <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Create project</p>
                  <h2 className="mt-1 font-display text-3xl font-bold">New project record</h2>
                </div>
                <form onSubmit={handleCreateProject} className="mt-5 grid gap-4">
                  <Field label="Brand" dark>
                    <select
                      value={createForm.brand}
                      onChange={(event) => {
                        const nextBrand = event.target.value as BrandValue;
                        setCreateForm(blankCreateForm(nextBrand));
                        setSelectedBrand(nextBrand);
                      }}
                      className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                    >
                      {brandOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Client" dark>
                    <select
                      value={createForm.clientId}
                      onChange={(event) => {
                        const client = clients.find((item) => item.id === event.target.value);
                        setCreateForm((current) => ({
                          ...current,
                          clientId: event.target.value,
                          clientName: client?.clientName || current.clientName,
                          brand: (client?.brand === "truevance_connect" ? "truevance_connect" : current.brand) as BrandValue,
                        }));
                      }}
                      className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                    >
                      <option value="">Select a client</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.clientName} {client.organizationName ? `• ${client.organizationName}` : ""}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <TextInput label="Client name" dark value={createForm.clientName} onChange={(value) => updateCreateForm("clientName", value)} />
                  <TextInput label="Project name" dark value={createForm.projectName} onChange={(value) => updateCreateForm("projectName", value)} />
                  <TextInput label="Project type" dark value={createForm.projectType} onChange={(value) => updateCreateForm("projectType", value)} />
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Project status" dark>
                      <select
                        value={createForm.projectStatus}
                        onChange={(event) => updateCreateForm("projectStatus", event.target.value)}
                        className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                      >
                        {projectStatusOptions.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Project phase" dark>
                      <select
                        value={createForm.projectPhase}
                        onChange={(event) => updateCreateForm("projectPhase", event.target.value)}
                        className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                      >
                        {projectPhaseOptions.map((phase) => (
                          <option key={phase} value={phase}>
                            {phase}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Payment status" dark>
                      <select
                        value={createForm.paymentStatus}
                        onChange={(event) => updateCreateForm("paymentStatus", event.target.value)}
                        className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                      >
                        {paymentStatusOptions.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <TextInput label="Completion %" type="number" dark value={createForm.completionPercent} onChange={(value) => updateCreateForm("completionPercent", value)} />
                    <TextInput label="Revenue (cents)" type="number" dark value={createForm.revenueCents} onChange={(value) => updateCreateForm("revenueCents", value)} />
                    <TextInput label="Start date" type="date" dark value={createForm.startDate} onChange={(value) => updateCreateForm("startDate", value)} />
                    <TextInput label="Due date" type="date" dark value={createForm.dueDate} onChange={(value) => updateCreateForm("dueDate", value)} />
                  </div>
                  <TextInput label="Assigned team" dark value={createForm.assignedTeam} onChange={(value) => updateCreateForm("assignedTeam", value)} />
                  <TextArea label="Assigned services" dark value={createForm.assignedServicesText} onChange={(value) => updateCreateForm("assignedServicesText", value)} helper="Use one item per line or separate with commas." />
                  <TextArea label="Milestones" dark value={createForm.milestonesText} onChange={(value) => updateCreateForm("milestonesText", value)} helper="Use one item per line or separate with commas." />
                  <TextArea label="Deliverables" dark value={createForm.deliverablesText} onChange={(value) => updateCreateForm("deliverablesText", value)} helper="Use one item per line or separate with commas." />
                  <Field label="Portal access status" dark>
                    <select
                      value={createForm.portalAccessStatus}
                      onChange={(event) => updateCreateForm("portalAccessStatus", event.target.value)}
                      className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                    >
                      {portalAccessOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <TextArea label="Portal access notes" dark value={createForm.portalAccessNotes} onChange={(value) => updateCreateForm("portalAccessNotes", value)} />
                  <TextArea label="Project notes" dark value={createForm.notes} onChange={(value) => updateCreateForm("notes", value)} />
                  <div className="flex flex-wrap justify-end gap-3">
                    <button
                      type="submit"
                      disabled={creating}
                      className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                    >
                      <Plus className="size-4" aria-hidden="true" />
                      {creating ? "Creating..." : "Create project"}
                    </button>
                  </div>
                </form>
              </section>
            ) : null}

            <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
              <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Project list</p>
                <h2 className="mt-1 font-display text-3xl font-bold">Brand-scoped projects</h2>
              </div>
              <div className="mt-5 grid gap-3">
                {loadingProjects ? (
                  <div className="business-center-os-loading rounded-3xl p-5">
                    <p className="text-sm font-semibold text-[#CBD5E1]">Loading project records for the selected brand.</p>
                  </div>
                ) : filteredProjects.length ? (
                  filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() => {
                        setSelectedProjectId(project.id);
                        navigate(`/admin/truevance-project-center/${project.id}`);
                      }}
                      className={`business-center-os-card business-center-os-card--light text-left rounded-3xl p-4 transition hover:-translate-y-0.5 ${
                        selectedProjectId === project.id ? "ring-2 ring-[#06B6D4]" : ""
                      }`}
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="grid gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-display text-2xl font-bold text-[#1F2937]">{project.projectName || "Untitled project"}</h3>
                            <Badge tone={project.brand === "truevance_connect" ? "teal" : "orange"}>{brandLabel(project.brand as BrandValue)}</Badge>
                            <Badge tone="slate">{project.projectStatus}</Badge>
                          </div>
                          <p className="text-sm font-semibold leading-6 text-[#475569]">
                            {project.clientName || "Client not set"} • {project.projectType || "Project type not set"}
                          </p>
                          <p className="text-sm font-semibold leading-6 text-[#64748B]">
                            Phase {project.projectPhase || "Planning"} • {project.paymentStatus || "Pending payment"}
                          </p>
                        </div>
                        <div className="grid gap-2 text-sm font-semibold text-[#334155] lg:text-right">
                          <p>{project.projectNumber}</p>
                          <p>{formatCurrency(project.revenueCents)}</p>
                        </div>
                      </div>
                      <p className="mt-3 text-sm font-semibold leading-6 text-[#475569]">
                        {project.notes || "No project notes yet. Open the record to review progress and activity."}
                      </p>
                    </button>
                  ))
                ) : (
                  <div className="business-center-os-empty rounded-3xl p-6 text-center">
                    <p className="font-display text-2xl font-bold text-[#1F2937]">No project records for this brand yet</p>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">
                      Create the first brand-scoped project record or accept a proposal to generate one automatically.
                    </p>
                  </div>
                )}
              </div>
            </section>
          </aside>

          <section className="grid gap-8">
            {message ? (
              <div className="business-center-os-card business-center-os-card--light rounded-3xl p-5">
                <p className="text-sm font-semibold leading-7 text-[#1F2937]">{message}</p>
              </div>
            ) : null}

            {!selectedProject || loadingDetail || !detail || !editForm ? (
              <div className="business-center-os-empty rounded-3xl p-8">
                <p className="font-display text-3xl font-bold text-[#1F2937]">
                  {loadingDetail ? "Loading project record" : "Select a project record"}
                </p>
                <p className="mt-3 max-w-2xl text-sm font-semibold leading-7 text-[#475569]">
                  {loadingDetail
                    ? "The selected project, linked client, proposal, agreement, payment, and timeline are loading."
                    : "Choose a project on the left to review the record, update delivery details, and track the project activity feed."}
                </p>
              </div>
            ) : (
              <>
                <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                  <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-5 lg:flex-row lg:items-end lg:justify-between">
                    <div className="grid gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#06B6D4]">Project record</p>
                        <Badge tone={detail.project.brand === "truevance_connect" ? "teal" : "orange"}>{brandLabel(detail.project.brand as BrandValue)}</Badge>
                      </div>
                      <h2 className="font-display text-3xl font-bold">{detail.project.projectName}</h2>
                      <p className="text-sm font-semibold leading-7 text-[#CBD5E1]">
                        Brand-scoped project delivery record with linked client, proposal, agreement, payment, and activity
                        events.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to="/admin/business-center-os"
                        className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
                      >
                        <ArrowLeft className="size-4" aria-hidden="true" />
                        Back to Business Center OS
                      </Link>
                      <button
                        type="button"
                        onClick={() => void handleRefreshActivity()}
                        className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
                      >
                        <Clock3 className="size-4" aria-hidden="true" />
                        Refresh timeline
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleArchiveProject()}
                        disabled={archiving}
                        className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                        {archiving ? "Archiving..." : "Archive"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MetricCard label="Project status" value={detail.project.projectStatus} note={`Phase ${detail.project.projectPhase}`} tone="dark" />
                    <MetricCard label="Payment status" value={detail.project.paymentStatus} note={formatCurrency(detail.project.revenueCents)} tone="light" />
                    <MetricCard label="Completion" value={`${detail.project.completionPercent}%`} note={detail.project.assignedTeam || "No team assigned"} tone="light" />
                    <MetricCard label="Updated" value={formatDate(detail.project.updatedAt)} note={detail.project.projectNumber} tone="dark" />
                  </div>

                  <form onSubmit={handleSaveProject} className="mt-6 grid gap-5">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label="Brand" dark>
                        <select
                          value={editForm.brand}
                          onChange={(event) => updateEditForm("brand", event.target.value as BrandValue)}
                          className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                        >
                          {brandOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Client" dark>
                        <select
                          value={editForm.clientId}
                          onChange={(event) => {
                            const client = clients.find((item) => item.id === event.target.value);
                            setEditForm((current) =>
                              current
                                ? {
                                    ...current,
                                    clientId: event.target.value,
                                    clientName: client?.clientName || current.clientName,
                                  }
                                : current,
                            );
                          }}
                          className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                        >
                          <option value="">Select a client</option>
                          {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.clientName} {client.organizationName ? `• ${client.organizationName}` : ""}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <TextInput label="Client name" dark value={editForm.clientName} onChange={(value) => updateEditForm("clientName", value)} />
                      <TextInput label="Project name" dark value={editForm.projectName} onChange={(value) => updateEditForm("projectName", value)} />
                      <TextInput label="Project type" dark value={editForm.projectType} onChange={(value) => updateEditForm("projectType", value)} />
                      <Field label="Project status" dark>
                        <select
                          value={editForm.projectStatus}
                          onChange={(event) => updateEditForm("projectStatus", event.target.value)}
                          className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                        >
                          {projectStatusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Project phase" dark>
                        <select
                          value={editForm.projectPhase}
                          onChange={(event) => updateEditForm("projectPhase", event.target.value)}
                          className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                        >
                          {projectPhaseOptions.map((phase) => (
                            <option key={phase} value={phase}>
                              {phase}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Payment status" dark>
                        <select
                          value={editForm.paymentStatus}
                          onChange={(event) => updateEditForm("paymentStatus", event.target.value)}
                          className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                        >
                          {paymentStatusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <TextInput label="Assigned team" dark value={editForm.assignedTeam} onChange={(value) => updateEditForm("assignedTeam", value)} />
                      <TextInput label="Completion %" type="number" dark value={editForm.completionPercent} onChange={(value) => updateEditForm("completionPercent", value)} />
                      <TextInput label="Revenue (cents)" type="number" dark value={editForm.revenueCents} onChange={(value) => updateEditForm("revenueCents", value)} />
                      <TextInput label="Start date" type="date" dark value={editForm.startDate} onChange={(value) => updateEditForm("startDate", value)} />
                      <TextInput label="Due date" type="date" dark value={editForm.dueDate} onChange={(value) => updateEditForm("dueDate", value)} />
                      <Field label="Portal access status" dark>
                        <select
                          value={editForm.portalAccessStatus}
                          onChange={(event) => updateEditForm("portalAccessStatus", event.target.value)}
                          className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                        >
                          {portalAccessOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <TextArea label="Assigned services" dark value={editForm.assignedServicesText} onChange={(value) => updateEditForm("assignedServicesText", value)} />
                      <TextArea label="Milestones" dark value={editForm.milestonesText} onChange={(value) => updateEditForm("milestonesText", value)} />
                      <TextArea label="Deliverables" dark value={editForm.deliverablesText} onChange={(value) => updateEditForm("deliverablesText", value)} />
                      <TextArea label="Portal access notes" dark value={editForm.portalAccessNotes} onChange={(value) => updateEditForm("portalAccessNotes", value)} />
                      <TextArea label="Project notes" dark value={editForm.notes} onChange={(value) => updateEditForm("notes", value)} />
                      <TextArea label="Add activity note" dark value={editForm.activityNote} onChange={(value) => updateEditForm("activityNote", value)} helper="Saved as a new timeline event when you click Save project." />
                    </div>

                    <div className="flex flex-wrap justify-end gap-3">
                      <button
                        type="submit"
                        disabled={saving}
                        className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                      >
                        {saving ? "Saving..." : "Save project"}
                      </button>
                    </div>
                  </form>
                </section>

                <section className="grid gap-8 lg:grid-cols-2">
                  <ProjectRelationPanel
                    title="Client record"
                    eyebrow="Linked client"
                    tone="light"
                    emptyMessage="This project is not linked to a client yet."
                    detail={detail.client ? [
                      { label: "Client", value: detail.client.clientName },
                      { label: "Brand", value: brandLabel(detail.client.brand as BrandValue) },
                      { label: "Organization", value: detail.client.organizationName || "Not set" },
                      { label: "Contact", value: detail.client.contactName || "Not set" },
                      { label: "Email", value: detail.client.clientEmail || "Not set" },
                      { label: "Portal status", value: detail.client.portalAccessStatus || "Not set" },
                    ] : []}
                    link={detail.client ? "/admin/truevance-client-center" : undefined}
                  />

                  <ProjectRelationPanel
                    title="Proposal record"
                    eyebrow="Linked proposal"
                    tone="light"
                    emptyMessage="No proposal is linked to this project yet."
                    detail={detail.proposal ? [
                      { label: "Proposal", value: detail.proposal.proposalNumber },
                      { label: "Status", value: detail.proposal.status },
                      { label: "Package", value: detail.proposal.packageName || "Not set" },
                      { label: "Services", value: detail.proposal.selectedServices || "Not set" },
                      { label: "Add-ons", value: detail.proposal.recommendedAddons || "Not set" },
                    ] : []}
                    link={detail.proposal?.reviewUrl || (detail.proposal ? "/admin/proposals" : undefined)}
                  />

                  <ProjectRelationPanel
                    title="Agreement record"
                    eyebrow="Linked agreement"
                    tone="light"
                    emptyMessage="No agreement is linked to this project yet."
                    detail={detail.agreement ? [
                      { label: "Agreement", value: detail.agreement.agreementNumber },
                      { label: "Status", value: detail.agreement.status },
                      { label: "Provider", value: detail.agreement.provider },
                      { label: "Signed", value: detail.agreement.signedAt ? formatDate(detail.agreement.signedAt) : "Not signed" },
                    ] : []}
                    link={detail.agreement ? `/admin/agreement-records/${detail.agreement.id}` : undefined}
                  />

                  <ProjectRelationPanel
                    title="Payment record"
                    eyebrow="Linked payment"
                    tone="light"
                    emptyMessage="No payment is linked to this project yet."
                    detail={detail.payment ? [
                      { label: "Invoice", value: detail.payment.invoiceNumber },
                      { label: "Status", value: detail.payment.status },
                      { label: "Invoice status", value: detail.payment.invoiceStatus },
                      { label: "Amount paid", value: formatCurrency(detail.payment.amountPaidCents) },
                      { label: "Remaining", value: formatCurrency(detail.payment.amountRemainingCents) },
                    ] : []}
                    link={detail.payment ? `/admin/payment-records/${detail.payment.id}` : undefined}
                  />
                </section>

                <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                  <div className="flex flex-col gap-3 border-b border-[rgba(6,182,212,.16)] pb-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Activity timeline</p>
                      <h3 className="mt-1 font-display text-3xl font-bold">Project events</h3>
                    </div>
                    <p className="text-sm font-semibold leading-7 text-[#CBD5E1]">
                      Timeline entries preserve the project history as it moves through delivery.
                    </p>
                  </div>
                  <div className="mt-5 grid gap-3">
                    {detail.timeline.length ? (
                      detail.timeline.map((item) => (
                        <article key={item.id} className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.05)] p-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge tone="teal">{labelize(item.eventType)}</Badge>
                                <h4 className="font-display text-xl font-bold text-[#F8FAFC]">{item.title}</h4>
                              </div>
                              <p className="mt-2 text-sm font-semibold leading-7 text-[#CBD5E1]">{item.detail}</p>
                            </div>
                            <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
                              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FDBA74]">{formatDate(item.createdAt)}</p>
                              {item.relatedLink ? (
                                <Link
                                  to={item.relatedLink}
                                  className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-3 text-xs font-extrabold"
                                >
                                  Open record
                                  <ExternalLink className="size-3" aria-hidden="true" />
                                </Link>
                              ) : null}
                            </div>
                          </div>
                        </article>
                      ))
                    ) : (
                      <div className="business-center-os-empty rounded-3xl p-6">
                        <p className="font-display text-2xl font-bold text-[#1F2937]">No timeline events yet</p>
                        <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">
                          Project saves, activity notes, and status changes will appear here.
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}
          </section>
        </div>
      </section>
    </BusinessCenterOSAdminShell>
  );
}

function ProjectRelationPanel({
  title,
  eyebrow,
  tone,
  emptyMessage,
  detail,
  link,
}: {
  title: string;
  eyebrow: string;
  tone: "light" | "dark";
  emptyMessage: string;
  detail: Array<{ label: string; value: string }>;
  link?: string;
}) {
  return (
    <section className={`business-center-os-card business-center-os-card--${tone} rounded-3xl p-5 sm:p-6`}>
      <div className="border-b border-[rgba(31,41,55,.12)] pb-4">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">{eyebrow}</p>
        <h3 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">{title}</h3>
      </div>
      {detail.length ? (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {detail.map((item) => (
              <InfoTile key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
          {link ? (
            <Link
              to={link}
              className="business-center-os-button business-center-os-button--secondary focus-ring mt-4 inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
            >
              Open linked record
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          ) : null}
        </>
      ) : (
        <div className="mt-5 rounded-2xl border border-[rgba(20,184,166,.16)] bg-[rgba(20,184,166,.08)] p-4">
          <p className="text-sm font-semibold leading-7 text-[#1F2937]">{emptyMessage}</p>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  children,
  dark = false,
}: {
  label: string;
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      {children}
    </label>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  dark = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  dark?: boolean;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        className={`business-center-os-field ${dark ? "business-center-os-field--dark" : ""} focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold`}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  dark = false,
  helper,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  dark?: boolean;
  helper?: string;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold md:col-span-2 ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className={`business-center-os-field ${dark ? "business-center-os-field--dark" : ""} focus-ring rounded-2xl px-4 py-3 text-sm font-semibold`}
      />
      {helper ? <span className="text-xs font-semibold tracking-normal text-[#CBD5E1]">{helper}</span> : null}
    </label>
  );
}

function MetricCard({ label, value, note, tone }: { label: string; value: string | number; note: string; tone: "light" | "dark" }) {
  return (
    <article className={`rounded-2xl border ${tone === "dark" ? "border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.05)]" : "border-[rgba(20,184,166,.16)] bg-white"} p-4`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${tone === "dark" ? "text-[#06B6D4]" : "text-[#14B8A6]"}`}>{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${tone === "dark" ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>{value}</p>
      <p className={`mt-2 text-sm font-semibold leading-6 ${tone === "dark" ? "text-[#CBD5E1]" : "text-[#475569]"}`}>{note}</p>
    </article>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{label}</p>
      <p className="mt-2 break-words text-sm font-bold leading-6 text-[#1F2937]">{value}</p>
    </div>
  );
}

function Badge({ children, tone }: { children: ReactNode; tone: "teal" | "orange" | "slate" }) {
  const tones = {
    teal: "border-[rgba(6,182,212,.18)] bg-[rgba(6,182,212,.1)] text-[#0F766E]",
    orange: "border-[rgba(255,107,53,.2)] bg-[rgba(255,107,53,.1)] text-[#C2410C]",
    slate: "border-[rgba(31,41,55,.12)] bg-[rgba(15,23,42,.06)] text-[#334155]",
  } as const;
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.16em] ${tones[tone]}`}>{children}</span>;
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    const fullTitle = `${title} | Business Center OS`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#1F2937");

    const faviconHref = buildBusinessCenterFavicon();
    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const icons = iconSelectors
      .map((selector) => document.querySelector<HTMLLinkElement>(selector))
      .filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#FF6B35"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#1F2937"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <path d="M33 16h15c-4 3.3-6.8 7.6-6.8 13.6 0 6.2 3.2 10.6 7.8 15.4H33c-8.8 0-16-6.4-16-14.5S24.2 16 33 16Z" fill="#F8FAFC" opacity=".1"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function labelize(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function brandLabel(value?: BrandValue | string) {
  return value === "truevance_connect" ? "Truevance Connect" : "Kreatethedate";
}

function splitList(value: string) {
  return value
    .split(/[\n,]/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatCurrency(cents = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format((Number(cents || 0) || 0) / 100);
}
